import { useContext } from "react";
import { useAuthUser } from "react-auth-kit";
import { fetchWithErrorHandling, buildPostOptions, buildGetOptions } from "../FetchUtils";
import { AppContext } from "../../utils/AppContext";
import { IDataEntity } from "./DataSchemaService";
import { getBaseURL } from "utils/Environment";

export const listDataStructures = async (baseURL: string, accessToken: string) => {
  return fetchWithErrorHandling(baseURL + `v1/data/list-data-structures`, buildPostOptions(accessToken));
};

export const listDataEntitiesId = async (
  baseURL: string,
  accessToken: string,
  dataStructureName: string,
  limit?: number,
  cursor?: string,
) => {
  const queryParams = new URLSearchParams();

  if (limit) {
    queryParams.set("limit", limit.toString());
  }

  if (cursor) {
    queryParams.set("cursor", cursor);
  }

  return fetchWithErrorHandling(
    baseURL + `v1/data/${dataStructureName}/load-ids?${queryParams.toString()}`,
    buildPostOptions(accessToken),
  );
};

export const loadDataEntity = async (baseURL: string, accessToken: string, dataStructureName: string, id: string) => {
  return fetchWithErrorHandling(baseURL + `v1/data/${dataStructureName}/load/${id}`, buildPostOptions(accessToken));
};

export const storeDataEntity = async (baseURL: string, accessToken: string, dataStructureName: string, content: unknown) => {
  return fetchWithErrorHandling(baseURL + `v1/data/${dataStructureName}/store`, buildPostOptions(accessToken, content));
};

export const archiveDataEntity = async (
  baseURL: string,
  accessToken: string,
  dataStructureName: string,
  structureTfid: string,
) => {
  return fetchWithErrorHandling(
    baseURL + `v1/data/${dataStructureName}/archive/${structureTfid}`,
    buildPostOptions(accessToken),
  );
};

export const reviveDataEntity = async (
  baseURL: string,
  accessToken: string,
  dataStructureName: string,
  structureTfid: string,
) => {
  return fetchWithErrorHandling(
    baseURL + `v1/data/${dataStructureName}/revive/${structureTfid}`,
    buildPostOptions(accessToken),
  );
};

export const exportData = async (
  baseURL: string,
  accessToken: string,
  ids: string[],
): Promise<{ list: IDataEntity[]; errors: string[] }> => {
  return fetchWithErrorHandling(baseURL + `v1/data/export`, buildPostOptions(accessToken, { ids }));
};

export const importData = async (baseURL: string, accessToken: string, data: IDataEntity[]) => {
  return fetchWithErrorHandling(baseURL + `v1/data/import`, buildPostOptions(accessToken, { data }));
};

export const loadAllPersistenceLayers = async (baseURL: string, accessToken: string) => {
  return fetchWithErrorHandling(baseURL + `v1/data/persistence-layers`, buildGetOptions(accessToken));
};

export const executeRedisCommand = async (
  baseURL: string,
  accessToken: string,
  command: string,
  args?: string[],
): Promise<{ result: unknown }> => {
  return fetchWithErrorHandling(
    baseURL + `v1/data/redis-command`,
    buildPostOptions(accessToken, { command, args: args ?? [] }),
  );
};

export const scanOnS3 = async (
  baseURL: string,
  accessToken: string,
  searchExpression: string,
): Promise<{ keys: string[] }> => {
  return fetchWithErrorHandling(baseURL + `v1/data/scan-on-s3`, buildPostOptions(accessToken, { searchExpression }));
};

export const useDataPersistenceService = () => {
  const appContext = useContext(AppContext);
  const authUser = useAuthUser();
  const accessToken = authUser()?.access_token;

  return {
    listDataStructures: () => listDataStructures(getBaseURL(appContext), accessToken),
    loadDataEntity: (dataStructureName: string, id: string) =>
      loadDataEntity(getBaseURL(appContext), accessToken, dataStructureName, id),
    listDataEntitiesId: (dataStructureName: string, limit?: number, cursor?: string) =>
      listDataEntitiesId(getBaseURL(appContext), accessToken, dataStructureName, limit, cursor),
    storeDataEntity: (dataStructureName: string, content: unknown) =>
      storeDataEntity(getBaseURL(appContext), accessToken, dataStructureName, content),
    archiveDataEntity: (dataStructureName: string, tfid: string) =>
      archiveDataEntity(getBaseURL(appContext), accessToken, dataStructureName, tfid),
    reviveDataEntity: (dataStructureName: string, tfid: string) =>
      reviveDataEntity(getBaseURL(appContext), accessToken, dataStructureName, tfid),
    exportData: (ids: string[]) => exportData(getBaseURL(appContext), accessToken, ids),
    importData: (data: IDataEntity[]) => importData(getBaseURL(appContext), accessToken, data),
    loadAllPersistenceLayers: () => loadAllPersistenceLayers(getBaseURL(appContext), accessToken),
    executeRedisCommand: (command: string, args?: string[]) =>
      executeRedisCommand(getBaseURL(appContext), accessToken, command, args),
    scanOnS3: (searchExpression: string) => scanOnS3(getBaseURL(appContext), accessToken, searchExpression),
  };
};
